import React, { useEffect, useState } from 'react';
import { serversAPI, clustersAPI } from '../services/api';

import { SidebarItem, BaremetalServerList, KubernetesNodeList, segmentStyle } from './server-components/ServerList';
import { ServerFormModal, AgentInstallModal, UnregisteredIPsPanel } from './server-components/ServerAgentPanel';
import { baremetalOf, totalServerCount, segmentCount } from '../utils/qaFixes';

const EMPTY_FORM = {
  name: '', ip_address: '', hostname: '',
  ssh_port: 22, ssh_user: 'root', ssh_private_key: '',
  provider: '', region: '', segment: '',
  connection_type: 'ssh',
};

export default function Servers() {
  const [servers, setServers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [clusterNodes, setClusterNodes] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [activeSegment, setActiveSegment] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // SSH test
  const [sshTesting, setSshTesting] = useState({});
  const [sshResult, setSshResult]   = useState({});

  // Agent token / install command modal
  const [agentModal, setAgentModal] = useState(null);

  // Unregistered IPs from alerts
  const [unregisteredIPs, setUnregisteredIPs] = useState([]);
  const [showUnregd, setShowUnregd] = useState(true);

  // Add-server modal step: 'pick_type' | 'form'
  const [addStep, setAddStep] = useState('pick_type');

  useEffect(() => {
    loadAll();
    serversAPI.unregisteredIPs().then(r => setUnregisteredIPs(r.data)).catch(() => {});
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [srvRes, segRes, allNodesRes] = await Promise.all([
        serversAPI.list(),
        serversAPI.listSegments(),
        clustersAPI.listAllNodes(),
      ]);
      setServers(srvRes.data);
      setSegments(segRes.data);

      const clusterList = allNodesRes.data;
      setClusters(clusterList);
      const nodeMap = {};
      clusterList.forEach(cl => { nodeMap[cl.id] = cl.nodes; });
      setClusterNodes(nodeMap);
    } catch (e) {
      console.error('Failed to load', e);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingServer(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setAddStep('pick_type');
    setShowModal(true);
  };

  const openEdit = (sv) => {
    setEditingServer(sv);
    setFormData({
      name: sv.name, ip_address: sv.ip_address || '', hostname: sv.hostname || '',
      ssh_port: sv.ssh_port || 22, ssh_user: sv.ssh_user || 'root',
      ssh_private_key: '', provider: sv.provider || '',
      region: sv.region || '', segment: sv.segment || '',
      connection_type: sv.connection_type || 'ssh',
      agent_scope: sv.agent_scope || 'read',
    });
    setFormError(null);
    setAddStep('form');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingServer(null);
    setFormError(null);
    setAddStep('pick_type');
  };

  const generateAgentToken = async (sv, scope) => {
    try {
      const res = await serversAPI.generateAgentToken(sv.id, scope);
      setAgentModal({ server: sv, ...res.data });
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate token');
    }
  };

  const installAgentOnNode = async (node, cluster) => {
    try {
      const payload = {
        name: node.node_name || node.label || `${cluster.name}-${node.role}`,
        ip_address: node.internal_ip || node.external_ip || '',
        hostname: node.hostname || node.node_name || '',
        connection_type: 'agent',
        segment: 'kubernetes',
      };
      const res = await serversAPI.create(payload);
      const tokenRes = await serversAPI.generateAgentToken(res.data.id, 'read');
      loadAll();
      setAgentModal({ server: res.data, ...tokenRes.data });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create server for agent');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const payload = { ...formData };
      if (!payload.segment) payload.segment = null;
      if (payload.connection_type === 'agent') {
        delete payload.ssh_private_key;
      }
      if (editingServer) {
        await serversAPI.update(editingServer.id, payload);
        if (payload.connection_type === 'agent' && payload.agent_scope) {
          await serversAPI.updateAgentScope(editingServer.id, payload.agent_scope);
        }
        closeModal();
        loadAll();
      } else {
        const res = await serversAPI.create(payload);
        closeModal();
        loadAll();
        if (payload.connection_type === 'agent') {
          const tokenRes = await serversAPI.generateAgentToken(res.data.id, payload.agent_scope || 'read');
          setAgentModal({ server: res.data, ...tokenRes.data });
          loadAll();
        }
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setFormError(typeof d === 'string' ? d : d ? JSON.stringify(d) : err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this server?')) return;
    try { await serversAPI.delete(id); loadAll(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete server'); }
  };

  const testSSH = async (sv) => {
    setSshTesting(prev => ({ ...prev, [sv.id]: 'testing' }));
    setSshResult(prev => ({ ...prev, [sv.id]: null }));
    try {
      const res = await serversAPI.testSSH(sv.id);
      setSshResult(prev => ({ ...prev, [sv.id]: res.data }));
      setSshTesting(prev => ({ ...prev, [sv.id]: res.data.ok ? 'ok' : 'fail' }));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Request failed';
      setSshResult(prev => ({ ...prev, [sv.id]: { ok: false, error: msg } }));
      setSshTesting(prev => ({ ...prev, [sv.id]: 'fail' }));
    }
  };

  // ── Computed values ───────────────────────────────────────────────────────────

  const allSegmentNames = [...new Set([
    ...segments.map(s => s.segment),
    'kubernetes',
  ])].filter(Boolean);

  // The BAREMETAL list must exclude servers tagged 'kubernetes' — those are
  // cluster nodes, shown in the Kubernetes section.
  // baremetalOf/totalServerCount/segmentCount live in src/utils/qaFixes.js and
  // are regression-tested (qaFixes.test.js).
  const baremetalServers = baremetalOf(servers);

  const filteredServers = baremetalServers.filter(sv => {
    if (activeSegment !== 'all' && activeSegment !== 'kubernetes') {
      return (sv.segment || 'unassigned') === activeSegment;
    }
    return true;
  });

  const serversBySegment = filteredServers.reduce((acc, sv) => {
    const seg = sv.segment || 'unassigned';
    if (!acc[seg]) acc[seg] = [];
    acc[seg].push(sv);
    return acc;
  }, {});

  const showBaremetal = typeFilter !== 'kubernetes' && activeSegment !== 'kubernetes';
  const showKubernetes = typeFilter !== 'baremetal';

  const totalK8sNodes = Object.values(clusterNodes).reduce((a, n) => a + n.length, 0);
  // True inventory total = baremetal + live k8s nodes (NOT raw servers.length,
  // which double-counts the kubernetes-tagged duplicate rows).
  const totalServers = totalServerCount(servers, totalK8sNodes);

  if (loading) return <div style={{ padding: 40, color: 'var(--muted)' }}>Loading servers...</div>;

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: '100%' }}>

      {/* ── Left Sidebar: Segment navigation ─────────────────────────────────── */}
      <div style={{
        width: 220, flexShrink: 0, borderRight: '1px solid var(--border)',
        paddingRight: 0, marginRight: 24,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
          Segments
        </div>

        <SidebarItem
          active={activeSegment === 'all'}
          onClick={() => { setActiveSegment('all'); setTypeFilter('all'); }}
          label="All Servers"
          count={totalServers}
          color="var(--ink-2)"
        />

        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--border-strong)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '12px 0 4px', paddingLeft: 8 }}>
          Baremetal
        </div>
        {allSegmentNames.filter(s => s !== 'kubernetes').map(seg => {
          const info = segments.find(s => s.segment === seg);
          return (
            <SidebarItem
              key={seg}
              active={activeSegment === seg}
              onClick={() => { setActiveSegment(seg); setTypeFilter('baremetal'); }}
              label={seg.charAt(0).toUpperCase() + seg.slice(1)}
              /* Count from the baremetal set (excludes kubernetes-tagged rows),
                 not backend server_count. */
              count={segmentCount(servers, seg)}
              color={segmentStyle(seg).dot}
            />
          );
        })}
        <SidebarItem
          active={activeSegment === 'unassigned' && typeFilter === 'baremetal'}
          onClick={() => { setActiveSegment('unassigned'); setTypeFilter('baremetal'); }}
          label="Unassigned"
          count={segmentCount(servers, 'unassigned')}
          color="var(--muted-2)"
        />

        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--border-strong)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '12px 0 4px', paddingLeft: 8 }}>
          Kubernetes
        </div>
        {clusters.map(cl => (
          <SidebarItem
            key={cl.id}
            active={activeSegment === `k8s-${cl.id}`}
            onClick={() => { setActiveSegment(`k8s-${cl.id}`); setTypeFilter('kubernetes'); }}
            label={cl.name}
            count={clusterNodes[cl.id]?.length || 0}
            color="var(--brand)"
          />
        ))}
      </div>

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>
              {activeSegment === 'all' ? 'All Servers' :
               activeSegment.startsWith('k8s-') ? clusters.find(c => `k8s-${c.id}` === activeSegment)?.name + ' Nodes' :
               activeSegment.charAt(0).toUpperCase() + activeSegment.slice(1) + ' Servers'}
            </h1>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
              {totalServers} servers &nbsp;·&nbsp; {baremetalServers.length} baremetal &nbsp;·&nbsp; {totalK8sNodes} k8s nodes across {clusters.length} clusters
            </div>
          </div>
          <button className="btn btn-primary" onClick={openAdd}>
            + Add Server
          </button>
        </div>

        {/* Type toggle (only shown in All view) */}
        {activeSegment === 'all' && (
          <div className="filter-tabs" style={{ marginBottom: 20 }}>
            {[['all', 'All'], ['baremetal', 'Baremetal'], ['kubernetes', 'Kubernetes']].map(([val, label]) => (
              <button key={val} onClick={() => setTypeFilter(val)} className={typeFilter === val ? 'active' : ''}>{label}</button>
            ))}
          </div>
        )}

        {/* Baremetal sections grouped by segment */}
        {showBaremetal && (
          <>
            {Object.entries(serversBySegment).length === 0 && typeFilter !== 'kubernetes' && (
              <div className="card" style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>
                No baremetal servers in this segment.
              </div>
            )}
            <BaremetalServerList
              serversBySegment={serversBySegment}
              sshResult={sshResult}
              sshTesting={sshTesting}
              onEdit={openEdit}
              onDelete={handleDelete}
              onTestSSH={testSSH}
              onGenerateAgentToken={generateAgentToken}
            />
          </>
        )}

        {/* Kubernetes sections grouped by cluster */}
        {showKubernetes && (
          <>
            {(activeSegment === 'all' || activeSegment === 'kubernetes') && (
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 16 }}>
                Kubernetes Nodes
              </div>
            )}
            <KubernetesNodeList
              clusters={clusters}
              clusterNodes={clusterNodes}
              activeSegment={activeSegment}
              servers={servers}
              sshResult={sshResult}
              sshTesting={sshTesting}
              onTestSSH={testSSH}
              onGenerateAgentToken={generateAgentToken}
              onInstallAgentOnNode={installAgentOnNode}
            />
          </>
        )}
      </div>

      {/* Modals */}
      <ServerFormModal
        showModal={showModal}
        editingServer={editingServer}
        addStep={addStep}
        setAddStep={setAddStep}
        formData={formData}
        setFormData={setFormData}
        formError={formError}
        submitting={submitting}
        onSubmit={handleSubmit}
        onClose={closeModal}
        onInputChange={handleInputChange}
      />

      <AgentInstallModal
        agentModal={agentModal}
        onClose={() => setAgentModal(null)}
      />

      {showUnregd && (
        <UnregisteredIPsPanel
          unregisteredIPs={unregisteredIPs}
          onDismiss={() => setShowUnregd(false)}
          onAddIP={(ip) => {
            setFormData({ ...EMPTY_FORM, ip_address: ip, connection_type: 'agent' });
            setAddStep('form');
            setEditingServer(null);
            setFormError(null);
            setShowModal(true);
          }}
          EMPTY_FORM={EMPTY_FORM}
        />
      )}
    </div>
  );
}
